import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { type StrapMapValue } from '@/utils/straps';

import { APOLLO_CLIENTS, CustomContext } from '@/graphql/client';
import {
  type OrderAppProductVariantsBusinessDetailsQuery,
  useProductVariantsBusinessDetailsQuery,
} from '@/graphql/orderApp/api';
import type { ProductVariantStrapDetailsQuery } from '@/graphql/saleor/api';
import { useProductVariantStrapDetailsQuery } from '@/graphql/saleor/api';
import { useBucklesAndTools } from '@/hooks/useBucklesAndTools';
import { useStrapSizeDatas } from '@/hooks/useStrapSizeDatas';
import { useStrapSizes } from '@/hooks/useStrapSizes';
import { getMultiselectValue } from '@/lib/attributes';
import { commonMessages } from '@/messages/common';
import { useRegionProvider } from '@/providers/RegionProvider';
import { checkoutActions } from '@/states/checkout';
import type { SizeState } from '@/views/StrapView/components';

export interface StrapData {
  businessData?: OrderAppProductVariantsBusinessDetailsQuery;
  variantDetailsData?: ProductVariantStrapDetailsQuery;
}

export const useStrapPurchaseFlow = (
  sku: string,
  variantId?: string,
  initialVariantGroup?: string[],
) => {
  const {
    query: { channel, languageCode },
  } = useRegionProvider();
  const { formatMessage } = useIntl();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [strapData, setStrapData] = useState<StrapData>({});

  const [strapSizeDatas, setStrapSizeDatas] = useState<
    StrapMapValue | undefined
  >();

  const [sizeState, setSizeState] = useState<SizeState>({
    isLoadingOtherSize: false,
    selectItems: [],
    sizesSelectedItem: variantId
      ? {
          label: '',
          value: variantId,
          data: {
            id: variantId,
            sku: sku,
          },
        }
      : null,
  });

  const { refetch: fetchVariantDetails } = useProductVariantStrapDetailsQuery({
    variables: {
      channel,
      languageCode,
      sku,
    },
    skip: true,
  });

  const { refetch: fetchBusinessData } = useProductVariantsBusinessDetailsQuery(
    {
      variables: { skus: initialVariantGroup ?? [sku], channel },
      context: CustomContext({
        client: APOLLO_CLIENTS.orderApp,
        disableErrorNotification: true,
      }),
      skip: true,
    },
  );

  const { buckles, tools, update } = useBucklesAndTools(
    strapData.variantDetailsData?.productVariant?.product.compatibleBuckles,
    null,
    true,
  );

  const skusForSizes = useMemo(() => {
    if (strapSizeDatas?.variant_group) {
      return strapSizeDatas.variant_group;
    }
    return initialVariantGroup ?? [sku];
  }, [strapSizeDatas, sku, initialVariantGroup]);

  useStrapSizeDatas(setStrapSizeDatas, sku);

  useStrapSizes(
    skusForSizes,
    variantId ?? '',
    true,
    setSizeState,
    sizeState,
    strapSizeDatas,
  );

  /**
   * Fetches strap details and business data to determine if a customization drawer should be opened.
   *
   * @param currentSku - The SKU of the strap to purchase.
   * @param variantGroup - Optional list of related variant SKUs.
   * @returns
   * - true: Drawer opened (has compatible buckles/tools).
   * - false: No drawer needed (direct add to bag).
   * - null: Error occurred (notified via global notification).
   */
  const handlePurchase = async (
    currentSku: string = sku,
    variantGroup: string[] = initialVariantGroup ?? [],
  ) => {
    try {
      const { data: variantDetails, errors: variantErrors } =
        await fetchVariantDetails({ sku: currentSku });
      if (variantErrors || !variantDetails?.productVariant) {
        throw new Error('Failed to fetch variant details');
      }

      const otherSizesSkus =
        variantGroup.length > 0
          ? variantGroup
          : getMultiselectValue(
              variantDetails.productVariant.product.otherSizesSkus,
            );
      const compatibleBucklesReferences = getMultiselectValue(
        variantDetails.productVariant.product.compatibleBuckles,
      );

      const { data: businessData, errors: businessErrors } =
        await fetchBusinessData({
          skus: Array.from(
            new Set([
              currentSku,
              ...otherSizesSkus,
              ...compatibleBucklesReferences,
            ]),
          ),
        });

      if (businessErrors || !businessData) {
        throw new Error('Failed to fetch business data');
      }

      setStrapData({
        variantDetailsData: variantDetails,
        businessData: businessData,
      });

      const updateResult = await update(
        variantDetails.productVariant.product.compatibleBuckles,
      );

      // Check if there are compatible buckles or tools to show in the drawer
      if (
        updateResult &&
        ((updateResult.buckles && updateResult.buckles.length > 0) ||
          (updateResult.tools && updateResult.tools.length > 0))
      ) {
        setIsDrawerOpen(true);
        return true; // Drawer opened, caller should stop direct purchase flow
      }

      return false; // No accessories found, caller should continue with direct add to bag
    } catch (error) {
      console.error('[useStrapPurchaseFlow] handlePurchase failed:', error);
      checkoutActions.notify(formatMessage(commonMessages.genericError));
      return null; // Error occurred, caller should stop flow
    }
  };

  return {
    handlePurchase,
    isDrawerOpen,
    setIsDrawerOpen,
    strapData,
    buckles,
    tools,
    /** UI state for the size selection dropdown */
    sizeState,
    setSizeState,
    /** Raw strap data from Algolia used for lookups (e.g., CTA, variant group) */
    strapSizeDatas,
  };
};
